import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  acceptAllProposals,
  acceptGroup,
  acceptGroupRepresentative,
  acceptIntake,
  acceptPreviewIntake,
  acceptProposal,
  askKnowledge,
  fetchUsageReport,
  reportDisagreement,
  analyzeLoop,
  applyCorrectionBatch,
  dismissLoop,
  fetchLoop,
  fetchOpenCorrectionBatches,
  rollbackCorrectionBatch,
  sendLoopMessage,
  type CorrectionBatch,
  type LoopAnswerState,
  type LoopState,
  confirmAssemblyFreshness,
  confirmFactFreshness,
  deferProposal,
  fetchDocumentVersions,
  fetchFacts,
  fetchIntake,
  fetchIntakeSettings,
  fetchKnowledgeHealth,
  fetchProposals,
  fetchProvenance,
  fetchStagedDocuments,
  fetchCanonDocuments,
  updateKnowledgeDocument,
  archiveKnowledgeDocument,
  fetchUpload,
  fetchUploads,
  fetchForwardRecipients,
  fetchSubjectCandidates,
  forwardProposal,
  type AcceptBinding,
  type ForwardRecipient,
  type SubjectCandidatesResponse,
  patchProposal,
  postDiscussionMessage,
  pushDocumentsToCanon,
  rejectGroup,
  rejectProposal,
  resolveConflict,
  rollbackDocument,
  splitProposal,
  updateFactValue,
  updateIntakeSettings,
  uploadKnowledge,
  TERMINAL_UPLOAD_STATUSES,
  type AcceptMode,
  type ConfirmedLabels,
  type FactsFilters,
  type FactsResponse,
  type IntakeFilters,
  type IntakeResponse,
  type IntakeSettings,
  type KnowledgeHealth,
  type KnowledgeProposal,
  type KnowledgeUpload,
  type LearningDocument,
  type KnowledgeBaseFilters,
  type KnowledgeBaseResponse,
  type KnowledgeDocumentPatch,
  type ProposalPatch,
  type RejectReason,
  type ResolveConflictBody,
  type SplitPart,
  type AnswerSource,
  type AssistantAnswer,
  createTestRun,
  fetchTestRun,
  fetchTestRuns,
  promoteTestSignal,
  resolveTestSignal,
  submitVerdict,
  type TestRunDetail,
  type TestRunHeader,
  type TestRunSource,
  type TestVerdict,
} from './api';

// Итерация 3 Фаза C — react-query хуки экрана обучения.

/** Список загрузок. Polling 3 сек, пока есть хоть одна не-терминальная (идёт разбор). */
export function useUploads() {
  return useQuery<KnowledgeUpload[]>({
    queryKey: ['knowledge-uploads'],
    queryFn: fetchUploads,
    refetchInterval: (query) => {
      const list = query.state.data ?? [];
      const anyActive = list.some((u) => !TERMINAL_UPLOAD_STATUSES.includes(u.status));
      return anyActive ? 3000 : false;
    },
    refetchIntervalInBackground: false,
  });
}

/** Одна загрузка с polling до терминального статуса. */
export function useUpload(id: number | null, enabled = true) {
  return useQuery<KnowledgeUpload>({
    queryKey: ['knowledge-upload', id],
    queryFn: () => fetchUpload(id as number),
    enabled: enabled && id !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && TERMINAL_UPLOAD_STATUSES.includes(status) ? false : 2000;
    },
    refetchIntervalInBackground: false,
  });
}

export function useUploadKnowledge(onProgress?: (p: number) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadKnowledge(file, onProgress),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge-uploads'] }),
  });
}

export function useProposals(uploadId: number | null, enabled = true) {
  return useQuery<KnowledgeProposal[]>({
    queryKey: ['knowledge-proposals', uploadId],
    queryFn: () => fetchProposals(uploadId as number),
    enabled: enabled && uploadId !== null,
  });
}

function useProposalInvalidation(uploadId: number) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['knowledge-proposals', uploadId] });
    qc.invalidateQueries({ queryKey: ['knowledge-upload', uploadId] });
    qc.invalidateQueries({ queryKey: ['knowledge-uploads'] });
  };
}

export function usePatchProposal(uploadId: number) {
  const invalidate = useProposalInvalidation(uploadId);
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: ProposalPatch }) => patchProposal(id, patch),
    onSuccess: invalidate,
  });
}

export function useAcceptProposal(uploadId: number) {
  const invalidate = useProposalInvalidation(uploadId);
  return useMutation({
    mutationFn: (id: number) => acceptProposal(id),
    onSuccess: invalidate,
  });
}

export function useRejectProposal(uploadId: number) {
  const invalidate = useProposalInvalidation(uploadId);
  return useMutation({
    // Legacy per-upload экран: «просто убрать» = trash (без журнала анти-повтора).
    mutationFn: (id: number) => rejectProposal(id, 'trash'),
    onSuccess: invalidate,
  });
}

export function useAcceptAll(uploadId: number) {
  const invalidate = useProposalInvalidation(uploadId);
  return useMutation({
    mutationFn: () => acceptAllProposals(uploadId),
    onSuccess: invalidate,
  });
}

// ----------------------------------------------------- Стол приёма (Фаза D)

/** Единый стол: сгруппированные входящие атомы всех источников. */
export function useIntake(filters: IntakeFilters) {
  return useQuery<IntakeResponse>({
    queryKey: ['knowledge-intake', filters],
    queryFn: () => fetchIntake(filters),
  });
}

function useIntakeInvalidation() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['knowledge-intake'] });
    qc.invalidateQueries({ queryKey: ['knowledge-uploads'] });
  };
}

export function useIntakeSettings() {
  return useQuery({
    queryKey: ['intake-settings'],
    queryFn: fetchIntakeSettings,
  });
}

/** Частичное обновление настроек стола: порог и/или дефолт приёма (F2). */
export function useUpdateIntakeSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<IntakeSettings>) => updateIntakeSettings(patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['intake-settings'] });
      qc.invalidateQueries({ queryKey: ['knowledge-intake'] });
    },
  });
}

/** Сводка анти-каши перед массовым принятием (без записи). */
export function useAcceptPreviewIntake() {
  return useMutation({
    mutationFn: (uploadId?: number) => acceptPreviewIntake(uploadId),
  });
}

export function useAcceptIntake() {
  const invalidate = useIntakeInvalidation();
  return useMutation({
    mutationFn: ({ mode, uploadId }: { mode: AcceptMode; uploadId?: number }) =>
      acceptIntake(mode, uploadId),
    onSuccess: invalidate,
  });
}

export function useAcceptGroup() {
  const invalidate = useIntakeInvalidation();
  return useMutation({
    mutationFn: ({ groupKey, mode }: { groupKey: string; mode?: AcceptMode }) =>
      acceptGroup(groupKey, mode),
    onSuccess: invalidate,
  });
}

export function useRejectGroup() {
  const invalidate = useIntakeInvalidation();
  return useMutation({
    mutationFn: (groupKey: string) => rejectGroup(groupKey),
    onSuccess: invalidate,
  });
}

export function useAcceptGroupRepresentative() {
  const invalidate = useIntakeInvalidation();
  return useMutation({
    mutationFn: (groupKey: string) => acceptGroupRepresentative(groupKey),
    onSuccess: invalidate,
  });
}

/** Правка одного атома на столе (без привязки к uploadId — инвалидирует стол). */
export function usePatchIntakeProposal() {
  const invalidate = useIntakeInvalidation();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: ProposalPatch }) => patchProposal(id, patch),
    onSuccess: invalidate,
  });
}

export function useAcceptIntakeProposal() {
  const invalidate = useIntakeInvalidation();
  return useMutation({
    mutationFn: ({ id, labels, binding }: { id: number; labels?: ConfirmedLabels; binding?: AcceptBinding }) =>
      acceptProposal(id, labels, binding),
    onSuccess: invalidate,
  });
}

/** Кандидаты привязки субъекта (Блок 3.5). Тянутся лениво — только когда карточке нужна привязка. */
export function useSubjectCandidates(proposalId: number, enabled: boolean) {
  return useQuery<SubjectCandidatesResponse>({
    queryKey: ['subject-candidates', proposalId],
    queryFn: () => fetchSubjectCandidates(proposalId),
    enabled,
    staleTime: 30_000,
  });
}

export function useRejectIntakeProposal() {
  const invalidate = useIntakeInvalidation();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: RejectReason }) => rejectProposal(id, reason),
    onSuccess: invalidate,
  });
}

// --- Дверь приёма (Блок 4b): решения над атомом ---

export function useDeferIntakeProposal() {
  const invalidate = useIntakeInvalidation();
  return useMutation({
    mutationFn: (id: number) => deferProposal(id),
    onSuccess: invalidate,
  });
}

export function useForwardRecipients() {
  return useQuery<ForwardRecipient[]>({
    queryKey: ['forward-recipients'],
    queryFn: fetchForwardRecipients,
  });
}

export function useForwardIntakeProposal() {
  const invalidate = useIntakeInvalidation();
  return useMutation({
    mutationFn: ({ id, toUserId, position }: { id: number; toUserId: number; position: string }) =>
      forwardProposal(id, toUserId, position),
    onSuccess: invalidate,
  });
}

/**
 * Полировка (з): сообщение владельца → ответ ИИ; обновляет стол (счётчики/discussing).
 * Саму нить НЕ инвалидируем через query — она живёт в локальном state «Карты знания»
 * и дописывается ответом локально; при повторном открытии карты грузится свежей с BE.
 */
export function useSendDiscussionMessage() {
  const invalidate = useIntakeInvalidation();
  return useMutation({
    mutationFn: ({ id, message }: { id: number; message: string }) => postDiscussionMessage(id, message),
    onSuccess: invalidate,
  });
}

export function useSplitIntakeProposal() {
  const invalidate = useIntakeInvalidation();
  return useMutation({
    mutationFn: ({ id, parts }: { id: number; parts: SplitPart[] }) => splitProposal(id, parts),
    onSuccess: invalidate,
  });
}

export function useResolveConflictProposal() {
  const invalidate = useIntakeInvalidation();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: ResolveConflictBody }) => resolveConflict(id, body),
    onSuccess: invalidate,
  });
}

/** Лениво подтянуть дерево источника для «Подробно» (по клику). */
export function useProvenance() {
  return useMutation({
    mutationFn: (id: number) => fetchProvenance(id),
  });
}

// --- Фаза F1: пульт здоровья (Д4) + экран фактов ---

/** Счётчики пульта; refetch раз в минуту — бейдж сайдбара живёт на этом же запросе. */
export function useKnowledgeHealth(enabled = true) {
  return useQuery<KnowledgeHealth>({
    queryKey: ['knowledge-health'],
    queryFn: fetchKnowledgeHealth,
    enabled,
    refetchInterval: 60000,
    refetchIntervalInBackground: false,
  });
}

export function useFacts(filters: FactsFilters) {
  return useQuery<FactsResponse>({
    queryKey: ['knowledge-facts', filters],
    queryFn: () => fetchFacts(filters),
  });
}

function useFactsInvalidation() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['knowledge-facts'] });
    qc.invalidateQueries({ queryKey: ['knowledge-health'] });
  };
}

/** Правка значения факта: каскад на backend метит зависимых «источник изменился». */
export function useUpdateFactValue() {
  const invalidate = useFactsInvalidation();
  return useMutation({
    mutationFn: ({ id, value }: { id: number; value: string }) => updateFactValue(id, value),
    onSuccess: invalidate,
  });
}

/** «Подтвердить свежесть»: единственный способ снять событийную пометку. */
export function useConfirmFactFreshness() {
  const invalidate = useFactsInvalidation();
  return useMutation({
    mutationFn: (id: number) => confirmFactFreshness(id),
    onSuccess: invalidate,
  });
}

/** То же для живой сборки (DW-fix: сборки протухают каскадом и видны на пульте). */
export function useConfirmAssemblyFreshness() {
  const invalidate = useFactsInvalidation();
  return useMutation({
    mutationFn: (id: number) => confirmAssemblyFreshness(id),
    onSuccess: invalidate,
  });
}

// --- F2: экран «К заливке» (предбанник Блока 5) ---

/** Списки экрана: staged — лежащие «к заливке», pushed — недавно залитые (под откат). */
export function useStagedDocuments(view: 'staged' | 'pushed' = 'staged') {
  return useQuery<LearningDocument[]>({
    queryKey: ['staged-docs', view],
    queryFn: () => fetchStagedDocuments(view),
  });
}

/** База знаний: браузер канон-карточек. queryKey включает фильтры — смена фильтра = свежий запрос. */
export function useCanonDocuments(filters: KnowledgeBaseFilters) {
  return useQuery<KnowledgeBaseResponse>({
    queryKey: ['knowledge-base', filters],
    queryFn: () => fetchCanonDocuments(filters),
  });
}

/** Инвалидация ленты «База знаний» после правки/архивации/заливки карточки (+ пульт Д4). */
function useKnowledgeBaseInvalidation() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['knowledge-base'] });
    qc.invalidateQueries({ queryKey: ['knowledge-health'] });
  };
}

/** Правка карточки базы знаний ин-плейс (заголовок/текст/видимость). */
export function useUpdateKnowledgeDocument() {
  const invalidate = useKnowledgeBaseInvalidation();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: KnowledgeDocumentPatch }) =>
      updateKnowledgeDocument(id, patch),
    onSuccess: invalidate,
  });
}

/** Убрать карточку из базы (в архив) — исчезает из ленты после рефетча. */
export function useArchiveKnowledgeDocument() {
  const invalidate = useKnowledgeBaseInvalidation();
  return useMutation({
    mutationFn: (id: number) => archiveKnowledgeDocument(id),
    onSuccess: invalidate,
  });
}

/**
 * Залить карточку(и) staged → канон с ленты «База знаний».
 * Переиспользует /learning/push (та же функция, что и экран «К заливке»), но
 * инвалидирует ленту базы знаний (+ staged-docs, чтобы предбанник тоже обновился).
 */
export function usePushToCanon() {
  const invalidate = useKnowledgeBaseInvalidation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (docIds: number[]) => pushDocumentsToCanon(docIds),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['staged-docs'] });
    },
  });
}

function useStagedInvalidation() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['staged-docs'] });
    qc.invalidateQueries({ queryKey: ['knowledge-health'] });
    // DW-LOW: модалка «Версии» не должна открываться со stale-цепочкой после push/отката.
    qc.invalidateQueries({ queryKey: ['doc-versions'] });
  };
}

/** Push выбранных/всех в канон — осознанная заливка руками владельца. */
export function usePushDocuments() {
  const invalidate = useStagedInvalidation();
  return useMutation({
    mutationFn: (docIds: number[]) => pushDocumentsToCanon(docIds),
    onSuccess: invalidate,
  });
}

/** Точечный откат одного канона (предшественник вернётся в бой сам). */
export function useRollbackDocument() {
  const invalidate = useStagedInvalidation();
  return useMutation({
    mutationFn: ({ id, to }: { id: number; to?: 'archived' | 'staged' }) => rollbackDocument(id, to),
    onSuccess: invalidate,
  });
}

/** История версий цепочки знания — для модалки «Версии». */
export function useDocumentVersions(id: number | null) {
  return useQuery<LearningDocument[]>({
    queryKey: ['doc-versions', id],
    queryFn: () => fetchDocumentVersions(id as number),
    enabled: id !== null,
  });
}

// --- F3: ассистент со следом ---

/** Задать вопрос базе знаний (платный AI-вызов — без авто-повторов). */
export function useAskKnowledge() {
  return useMutation<
    AssistantAnswer,
    unknown,
    { question: string; includeStaged?: boolean; recipientType?: import('./api').DeliveryRecipientType }
  >({
    mutationFn: ({ question, includeStaged, recipientType }) =>
      askKnowledge(question, includeStaged, recipientType),
  });
}

/** «Не согласен» на ответе ассистента (вход петли исправления). */
export function useReportDisagreement() {
  return useMutation<AssistantAnswer, unknown, { answerId: number; note?: string }>({
    mutationFn: ({ answerId, note }) => reportDisagreement(answerId, note),
  });
}

/** Д2-отчёт использования знаний: горячие | мёртвые. */
export function useUsageReport(sort: 'hot' | 'dead', enabled = true) {
  return useQuery<AnswerSource[]>({
    queryKey: ['knowledge-usage', sort],
    queryFn: () => fetchUsageReport(sort),
    enabled,
  });
}

// --- F4: петля исправления ---

/** Состояние петли по ответу (нить + источники + пакеты). */
export function useLoop(answerId: number | null, enabled = true) {
  return useQuery<LoopState>({
    queryKey: ['correction-loop', answerId],
    queryFn: () => fetchLoop(answerId as number),
    enabled: enabled && answerId !== null,
  });
}

/** Открытые пакеты правок (для стола приёмки). */
export function useOpenCorrectionBatches(enabled = true) {
  return useQuery<CorrectionBatch[]>({
    queryKey: ['correction-batches-open'],
    queryFn: fetchOpenCorrectionBatches,
    enabled,
  });
}

/** Разбор петли (платный AI; идемпотентен — без авто-повторов). */
export function useAnalyzeLoop() {
  return useMutation({ mutationFn: (answerId: number) => analyzeLoop(answerId) });
}

/** Фраза владельца → пакет правок (платный AI). Обновляет состояние петли. */
export function useSendLoopMessage(answerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (message: string) => sendLoopMessage(answerId, message),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['correction-loop', answerId] });
      qc.invalidateQueries({ queryKey: ['correction-batches-open'] });
    },
  });
}

/** Закрыть петлю без правок. */
export function useDismissLoop(answerId: number) {
  const qc = useQueryClient();
  return useMutation<LoopAnswerState>({
    mutationFn: () => dismissLoop(answerId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['correction-loop', answerId] });
      qc.invalidateQueries({ queryKey: ['correction-batches-open'] });
    },
  });
}

/** Подтвердить пакет (целиком/по позициям) → мост ревизия→канон. */
export function useApplyCorrectionBatch(answerId?: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ batchId, itemIds }: { batchId: number; itemIds?: number[] }) =>
      applyCorrectionBatch(batchId, itemIds),
    onSuccess: () => {
      if (answerId !== undefined) qc.invalidateQueries({ queryKey: ['correction-loop', answerId] });
      qc.invalidateQueries({ queryKey: ['correction-batches-open'] });
      qc.invalidateQueries({ queryKey: ['knowledge-facts'] });
    },
  });
}

/** Откатить применённый пакет. */
export function useRollbackCorrectionBatch(answerId?: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (batchId: number) => rollbackCorrectionBatch(batchId),
    onSuccess: () => {
      if (answerId !== undefined) qc.invalidateQueries({ queryKey: ['correction-loop', answerId] });
      qc.invalidateQueries({ queryKey: ['correction-batches-open'] });
    },
  });
}

// --- F5: тест-прогон знаний (Т4) ---

/** Список прогонов аккаунта (свежие первыми). */
export function useTestRuns(enabled = true) {
  return useQuery<TestRunHeader[]>({
    queryKey: ['test-runs'],
    queryFn: fetchTestRuns,
    enabled,
  });
}

/** Один прогон. Пока generating (генерация+прогон в Job) — опрашиваем; ready/failed — стоп. */
export function useTestRun(id: number | null) {
  return useQuery<TestRunDetail>({
    queryKey: ['test-run', id],
    queryFn: () => fetchTestRun(id as number),
    enabled: id !== null,
    refetchInterval: (query) => (query.state.data?.run.status === 'generating' ? 2500 : false),
    refetchIntervalInBackground: false,
  });
}

/** Запустить прогон (платная генерация+прогон — без авто-повторов). */
export function useCreateTestRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { source_type?: TestRunSource; include_staged?: boolean; doc_ids?: number[] }) =>
      createTestRun(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['test-runs'] }),
  });
}

/** Один тап вердикта (§3) → маршрут. Обновляет прогон + пульт Д4. */
export function useSubmitVerdict(runId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ questionId, verdict, note }: { questionId: number; verdict: TestVerdict; note?: string }) =>
      submitVerdict(questionId, verdict, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['test-run', runId] });
      qc.invalidateQueries({ queryKey: ['knowledge-health'] });
    },
  });
}

/** Закрыть карточку-сигнал. Связанный вопрос уходит с пульта Д4. */
export function useResolveTestSignal(runId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ signalId, status }: { signalId: number; status: 'resolved' | 'dismissed' }) =>
      resolveTestSignal(signalId, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['test-run', runId] });
      qc.invalidateQueries({ queryKey: ['knowledge-health'] });
    },
  });
}

/** «Дополнить базу»: карточка «Неполно» уходит на стол приёма (Обучение) предложением. */
export function usePromoteTestSignal(runId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ signalId }: { signalId: number }) => promoteTestSignal(signalId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['test-run', runId] });
      qc.invalidateQueries({ queryKey: ['knowledge-health'] });
      // Предложение появилось на столе приёма (Обучение) — обновим его список.
      qc.invalidateQueries({ queryKey: ['knowledge-intake'] });
    },
  });
}
